import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { LegalFooter } from "@/components/brand/LegalFooter";
import { useUserProfile, saveUserProfile, emptyProfile, type UserProfile } from "@/lib/pr21/userProfile";
import { trackEvent } from "@/lib/audit";

export const Route = createFileRoute("/ajustes/privacidad")({
  head: () => ({
    meta: [
      { title: "Privacidad y preferencias · VerseSport™" },
      { name: "description", content: "Administra marketing, notificaciones, personalización, experiencias patrocinadas y analytics." },
    ],
  }),
  component: PrivacySettingsPage,
});

type PrefKey =
  | "marketing_opt_in"
  | "analytics_opt_in"
  | "accepts_promotions"
  | "coupon_interest";

function PrivacySettingsPage() {
  const profile = useUserProfile();
  const [draft, setDraft] = useState<UserProfile>(() => profile ?? emptyProfile());

  const set = (patch: Partial<UserProfile>) => {
    setDraft((d) => {
      const next = { ...d, ...patch };
      saveUserProfile(next);
      void trackEvent("consent_updated", { surface: "settings", keys: Object.keys(patch) });
      return next;
    });
  };

  const toggle = (key: PrefKey) => set({ [key]: !draft[key] } as Partial<UserProfile>);

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/" />
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <header className="mb-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-primary">
            Privacidad
          </span>
          <h1 className="mt-2 text-2xl font-black text-foreground">Privacidad y preferencias</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Decide cómo personalizamos tu experiencia, qué te enviamos y cómo medimos el uso.
          </p>
        </header>

        <section className="space-y-3 rounded-xl border border-border bg-accent/5 p-4">
          <Pref
            label="Comunicaciones de marketing"
            help="Promociones, novedades y experiencias destacadas por email."
            checked={!!draft.marketing_opt_in}
            onChange={() => toggle("marketing_opt_in")}
          />
          <Pref
            label="Experiencias patrocinadas"
            help="Recibir promociones contextuales y experiencias de socios comerciales."
            checked={!!draft.accepts_promotions}
            onChange={() => toggle("accepts_promotions")}
          />
          <Pref
            label="Cupones y beneficios"
            help="Mostrar cupones, vouchers y beneficios disponibles."
            checked={!!draft.coupon_interest}
            onChange={() => toggle("coupon_interest")}
          />
          <Pref
            label="Analytics anónimos"
            help="Permitir analítica agregada para mejorar la plataforma."
            checked={!!draft.analytics_opt_in}
            onChange={() => toggle("analytics_opt_in")}
          />
          <div>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Notificaciones
              </span>
              <select
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={draft.notification_frequency}
                onChange={(e) =>
                  set({ notification_frequency: e.target.value as UserProfile["notification_frequency"] })
                }
              >
                <option value="high">Alta</option>
                <option value="medium">Media</option>
                <option value="low">Baja</option>
                <option value="off">Apagadas</option>
              </select>
            </label>
          </div>
        </section>

        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Consulta{" "}
            <Link to="/terminos" className="font-bold text-primary hover:underline">Términos</Link>{" "}y{" "}
            <Link to="/privacidad" className="font-bold text-primary hover:underline">Privacidad</Link>.
          </p>
          <button
            type="button"
            onClick={() => toast.success("Preferencias guardadas")}
            className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
          >
            Guardar
          </button>
        </div>

        <LegalFooter />
      </div>
    </main>
  );
}

function Pref({
  label, help, checked, onChange,
}: { label: string; help: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-start justify-between gap-3 rounded-md border border-border/40 bg-background px-3 py-2.5">
      <span>
        <span className="block text-sm font-bold text-foreground">{label}</span>
        <span className="block text-xs text-muted-foreground">{help}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 h-4 w-4 accent-primary"
      />
    </label>
  );
}